import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import { ExternalLink, BookOpen, ShoppingCart, ChevronLeft, ChevronRight, Star, LibraryBig } from 'lucide-react';
import SectionHeader from '@/components/shared/SectionHeader';
import { booksData } from '@/data/books';

// Per-book accent color (matches topic)
const ACCENTS = [
  '#fbbf24', // Vol 1 Python   - amber
  '#a78bfa', // Vol 2 AI       - violet
  '#22d3ee', // Vol 3 C+Python - cyan
  '#f97316', // Vol 4 C        - orange
  '#4ade80', // Vol 5 Linux    - green
  '#2dd4bf', // Vol 7 Learn    - teal
  '#6366f1', // Vol 8 Agents   - indigo
];

export default function Books() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'it' | 'en';

  const [active, setActive] = useState(0);
  const bookWrapRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);

  const total = booksData.length;
  const book = booksData[active];
  const accent = ACCENTS[active] ?? '#16ffbb';

  // Spring-based mouse-tracking 3D tilt
  const rawY = useMotionValue(-22);
  const rawX = useMotionValue(5);
  const rotateY = useSpring(rawY, { stiffness: 120, damping: 18 });
  const rotateX = useSpring(rawX, { stiffness: 120, damping: 18 });

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!bookWrapRef.current) return;
    const r = bookWrapRef.current.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    rawY.set(nx * 40 - 10);
    rawX.set(-ny * 25 + 3);
  }, [rawX, rawY]);

  const resetTilt = useCallback(() => {
    rawY.set(-22);
    rawX.set(5);
  }, [rawX, rawY]);

  const go = useCallback((d: number) => {
    setActive(prev => (prev + d + total) % total);
  }, [total]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  return (
    <section
      id="books"
      className="section bg-surface dark:bg-dark-bg-subtle border-b-2 border-border-subtle dark:border-border-dark overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-4">

        <SectionHeader
          icon={LibraryBig}
          tag={t('books.tag', 'Libri')}
          index={6}
          title={t('books.title')}
          subtitle={t('books.subtitle')}
        />

        {/* ─── Main stage ─── */}
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">

          {/* ─ Prev arrow ─ */}
          <button
            onClick={() => go(-1)}
            aria-label="Previous book"
            className="hidden lg:flex w-12 h-12 items-center justify-center rounded-full bg-white dark:bg-dark-bg-elevated border-2 border-border dark:border-primary/20 shadow-xl hover:border-primary hover:shadow-primary/30 transition-all duration-200 group shrink-0"
          >
            <ChevronLeft className="h-5 w-5 text-text-muted group-hover:text-primary transition-colors" />
          </button>

          {/* ─ 3D Book ─ */}
          <div className="relative flex items-center justify-center shrink-0">

            {/* Ambient color glow */}
            <motion.div
              animate={{ opacity: [0.25, 0.45, 0.25] }}
              transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
              className="absolute inset-0 blur-3xl rounded-full pointer-events-none scale-90"
              style={{ background: `radial-gradient(ellipse at center, ${accent}88 0%, transparent 70%)` }}
            />

            {/* Perspective container */}
            <div style={{ perspective: '1100px', perspectiveOrigin: '50% 50%' }}>
              {/* Persistent 3D frame - mouse tilt lives here */}
              <motion.div
                ref={bookWrapRef}
                style={{ rotateY, rotateX, width: 210, height: 294 }}
                animate={{ y: [0, -14, 0] }}
                transition={{ y: { repeat: Infinity, duration: 4.5, ease: 'easeInOut' } }}
                onMouseMove={handleMouseMove}
                onMouseLeave={resetTilt}
                onPointerDown={e => { dragStartX.current = e.clientX; }}
                onPointerUp={e => {
                  const dx = e.clientX - dragStartX.current;
                  if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
                }}
                className="relative cursor-pointer select-none"
              >
                {/* ── Cover layer (fades on change) ── */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    className="absolute inset-0 rounded-sm overflow-hidden"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.28 }}
                    style={{
                      boxShadow: `
                        8px 8px 30px rgba(0,0,0,0.5),
                        0 0 60px ${accent}40,
                        inset -3px 0 8px rgba(0,0,0,0.25)
                      `
                    }}
                  >
                    {book.coverImage ? (
                      <img
                        src={book.coverImage}
                        alt={book.title[lang]}
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/40 to-secondary/30 flex items-center justify-center">
                        <BookOpen className="h-20 w-20 text-primary/40" />
                      </div>
                    )}

                    {/* Spine shadow (left edge) */}
                    <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-black/50 via-black/20 to-transparent pointer-events-none" />

                    {/* Top edge shadow */}
                    <div className="absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-black/25 to-transparent pointer-events-none" />

                    {/* Diagonal light sheen */}
                    <div className="absolute inset-0 pointer-events-none"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 40%, rgba(255,255,255,0.06) 80%, transparent 100%)'
                      }}
                    />
                  </motion.div>
                </AnimatePresence>

                {/* Pages edge (right side) - always visible */}
                <div
                  className="absolute top-1 bottom-1 right-0 rounded-r-sm pointer-events-none"
                  style={{
                    width: 6,
                    transform: 'translateX(3px)',
                    background: 'linear-gradient(to left, #e8e4d8, #f5f0e8)',
                    boxShadow: '2px 0 4px rgba(0,0,0,0.15)',
                  }}
                />

                {/* Cast shadow on the ground */}
                <motion.div
                  animate={{ opacity: [0.5, 0.7, 0.5], scaleX: [0.9, 1.05, 0.9] }}
                  transition={{ repeat: Infinity, duration: 4.5, ease: 'easeInOut' }}
                  className="absolute -bottom-8 left-1/2 -translate-x-1/2 rounded-full blur-xl pointer-events-none"
                  style={{
                    width: '80%', height: 24,
                    background: `radial-gradient(ellipse, ${accent}60 0%, rgba(0,0,0,0.3) 60%, transparent 100%)`
                  }}
                />
              </motion.div>
            </div>
          </div>

          {/* ─ Info panel ─ */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.32, ease: 'easeOut' }}
              className="flex-1 flex flex-col text-center lg:text-left"
            >
              {/* Volume + Series */}
              <div className="flex items-center gap-3 mb-5 justify-center lg:justify-start flex-wrap">
                {book.volume && (
                  <span
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-black rounded-full text-white shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, boxShadow: `0 4px 20px ${accent}50` }}
                  >
                    <Star className="h-3 w-3" />
                    Vol. {book.volume}
                  </span>
                )}
                {book.series && (
                  <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-text-muted dark:text-gray-500">
                    {book.series}
                  </span>
                )}
              </div>

              {/* Title */}
              <h3 className="text-2xl sm:text-3xl font-black text-text-primary dark:text-white leading-tight mb-2">
                {book.title[lang]}
              </h3>

              {/* Subtitle */}
              {book.subtitle && (
                <p className="text-sm sm:text-base font-semibold mb-5" style={{ color: accent }}>
                  {book.subtitle[lang]}
                </p>
              )}

              {/* Description */}
              <p className="text-sm text-text-secondary dark:text-text-light leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
                {book.description[lang]}
              </p>

              {/* CTA */}
              <div className="flex justify-center lg:justify-start">
                <motion.a
                  href={book.amazonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.06, boxShadow: `0 24px 48px ${accent}55` }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                  className="inline-flex items-center gap-2.5 px-8 py-4 font-black text-sm rounded-2xl text-white dark:text-gray-900 shadow-xl"
                  style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}bb 100%)` }}
                >
                  <ShoppingCart className="h-5 w-5" />
                  {t('books.viewOnAmazon')}
                  <ExternalLink className="h-4 w-4" />
                </motion.a>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* ─ Next arrow ─ */}
          <button
            onClick={() => go(1)}
            aria-label="Next book"
            className="hidden lg:flex w-12 h-12 items-center justify-center rounded-full bg-white dark:bg-dark-bg-elevated border-2 border-border dark:border-primary/20 shadow-xl hover:border-primary hover:shadow-primary/30 transition-all duration-200 group shrink-0"
          >
            <ChevronRight className="h-5 w-5 text-text-muted group-hover:text-primary transition-colors" />
          </button>
        </div>

        {/* ─── Mobile prev/next ─── */}
        <div className="flex lg:hidden justify-center gap-4 mt-6">
          <button
            onClick={() => go(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white dark:bg-dark-bg-elevated border-2 border-border dark:border-primary/20 shadow-lg hover:border-primary group"
          >
            <ChevronLeft className="h-5 w-5 text-text-muted group-hover:text-primary" />
          </button>
          <span className="text-xs font-bold text-text-muted dark:text-gray-500 self-center tabular-nums">
            {active + 1} / {total}
          </span>
          <button
            onClick={() => go(1)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white dark:bg-dark-bg-elevated border-2 border-border dark:border-primary/20 shadow-lg hover:border-primary group"
          >
            <ChevronRight className="h-5 w-5 text-text-muted group-hover:text-primary" />
          </button>
        </div>

        {/* ─── Thumbnail filmstrip ─── */}
        <div className="flex justify-center items-end gap-2 sm:gap-3 mt-10 overflow-x-auto pb-3">
          {booksData.map((b, i) => {
            const isActive = i === active;
            const ac = ACCENTS[i] ?? '#16ffbb';
            return (
              <motion.button
                key={i}
                onClick={() => setActive(i)}
                title={b.title[lang]}
                animate={{
                  scale: isActive ? 1.18 : 0.85,
                  y: isActive ? -10 : 0,
                  opacity: isActive ? 1 : 0.5,
                }}
                whileHover={{ scale: isActive ? 1.18 : 0.95, opacity: 0.9 }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                className={`relative shrink-0 rounded-md overflow-hidden transition-all duration-200 ${
                  isActive ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-dark-bg-subtle' : ''
                }`}
                style={{
                  width: 50, height: 70,
                  ringColor: ac,
                  boxShadow: isActive
                    ? `0 8px 32px ${ac}60, 0 0 0 2px ${ac}`
                    : '0 2px 8px rgba(0,0,0,0.2)',
                }}
              >
                {b.coverImage ? (
                  <img src={b.coverImage} alt={b.title[lang]} className="w-full h-full object-cover" draggable={false} />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/20 flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-primary/40" />
                  </div>
                )}
                {isActive && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: `linear-gradient(to top, ${ac}44, transparent)` }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>

        {/* ─── Bottom CTA ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-12"
        >
          <a
            href="https://www.amazon.it/stores/author/B0CC29G6VV"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-primary to-secondary text-white dark:text-gray-900 font-bold rounded-xl hover:opacity-90 transition-all hover:scale-105 shadow-xl shadow-primary/40 text-sm sm:text-base"
          >
            <BookOpen className="h-5 w-5 flex-shrink-0" />
            {lang === 'it' ? 'Vedi tutti i libri su Amazon' : 'View all books on Amazon'}
            <ExternalLink className="h-4 w-4 flex-shrink-0" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
